<script setup lang="ts">
import type { HTMLAttributes } from 'vue';

import { cn } from '@/lib/utils';

import { useSidebar } from './utils';

defineOptions({
    inheritAttrs: false
});

const props = withDefaults(
    defineProps<{
        side?: 'left' | 'right';
        variant?: 'sidebar' | 'floating' | 'inset';
        collapsible?: 'offcanvas' | 'icon' | 'none';
        class?: HTMLAttributes['class'];
    }>(),
    {
        side: 'left',
        variant: 'sidebar',
        collapsible: 'offcanvas'
    }
);

const { state } = useSidebar();
</script>

<template>
    <div
        v-if="collapsible === 'none'"
        :class="
            cn(
                'flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground',
                props.class
            )
        "
        v-bind="$attrs"
    >
        <slot />
    </div>

    <div
        v-else
        class="group peer relative block"
        :data-state="state"
        :data-collapsible="state === 'collapsed' ? collapsible : ''"
        :data-variant="variant"
        :data-side="side"
    >
        <!-- This is what handles the sidebar gap on desktop  -->
        <div
            :class="
                cn(
                    'duration-200 relative h-full w-[--sidebar-width] bg-transparent transition-[width] ease-linear',
                    // On mobile the expanded sidebar overlays the page instead
                    // of squeezing it: it keeps reserving the collapsed width.
                    'max-md:!w-[--sidebar-width-icon]',
                    'group-data-[collapsible=offcanvas]:w-0',
                    'group-data-[side=right]:rotate-180',
                    variant === 'floating' || variant === 'inset'
                        ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]'
                        : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon]'
                )
            "
        />
        <div
            :class="
                cn(
                    'duration-200 absolute inset-y-0 z-10 flex w-[--sidebar-width] transition-[left,right,width] ease-linear',
                    side === 'left'
                        ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
                        : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
                    // Adjust the padding for floating and inset variants.
                    variant === 'floating' || variant === 'inset'
                        ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]'
                        : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon]',
                    props.class
                )
            "
            v-bind="$attrs"
        >
            <div
                data-sidebar="sidebar"
                class="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
            >
                <slot />
            </div>
        </div>
    </div>
</template>
